import type { TreeItemsStateType } from '#components/treeView';
import { extractFilterKeyFromNodeId, type AllFilterType } from '@carrefour-gcs/shared';
import { computed } from 'vue';
import type { FiltersTreeType } from '../kpisPerimeter.model';
import { useGetFilters } from './useGetFilters';

export const useGetFiltersTree = () => {
  const filters = useGetFilters();

  const filterToTreeItem = (nodeIds: string[], filterType: AllFilterType, labelsMap?: Record<string, string>) => {
    return nodeIds.reduce((tree, nodeId) => {
      const label = labelsMap?.[nodeId] ?? extractFilterKeyFromNodeId(nodeId);

      tree[nodeId] = {
        nodeId,
        filterType,
        label,
        state: 'checked'
      };

      return tree;
    }, {} as TreeItemsStateType);
  };

  return computed<FiltersTreeType>(() => {
    return {
      nomenclatureFilters: {
        ...filterToTreeItem(filters.value.sectorFilters, 'sector'),
        ...filterToTreeItem(filters.value.classFilters, 'class'),
        ...filterToTreeItem(filters.value.groupClassFilters, 'grpClass'),
        ...filterToTreeItem(filters.value.departmentFilters, 'department'),
        ...filterToTreeItem(filters.value.subClassFilters, 'subClass')
      },
      activityTypesFilters: filterToTreeItem(filters.value.activityTypeFilters, 'activityType'),
      barcodeFilters: filterToTreeItem(filters.value.barcodeFilters, 'barcode'),
      suppliersFilters: {
        ...filterToTreeItem(filters.value.subSupplierFilters, 'subSupplier'),
        ...filterToTreeItem(filters.value.localSupplierFilters, 'localSupplier')
      },
      brandsFilters: filterToTreeItem(filters.value.brandFilters, 'brand'),
      bemHoldingFilters: filterToTreeItem(filters.value.bemHoldingFilters, 'bemMainHolding'),
      bemMerchandiseFilters: filterToTreeItem(filters.value.bemMerchandiseFilters, 'bemMerchandise'),
      itemFilters: filterToTreeItem(filters.value.itemFilters, 'item'),
      integratedFranchisedFilters: filterToTreeItem(filters.value.integratedFranchisedFilters, 'integratedFranchised'),
      storesFilters: {
        ...filterToTreeItem(filters.value.storeFilters, 'store'),
        ...filterToTreeItem(filters.value.chainTypeFilters, 'chainType')
      },
      triptyqueFilters: filterToTreeItem(filters.value.triptyqueFilters, 'brandType'),
      storesRegionFilters: filterToTreeItem(filters.value.storesRegionFilters, 'region'),
      assortmentTypologyFilters: filterToTreeItem(filters.value.assortmentTypologyFilters, 'assortmentTypology'),
      productTypologyFilters: filterToTreeItem(filters.value.productTypologyFilters, 'productTypology'),
      competitorFilters: filterToTreeItem(filters.value.competitorFilters, 'competitor'),
      competitorBaseBarcodeFilters: filterToTreeItem(
        filters.value.competitorBaseBarcodeFilters,
        'competitorBaseBarcode'
      ),
      competitorBaseItemFilters: filterToTreeItem(filters.value.competitorBaseItemFilters, 'competitorBaseItem'),
      competitorComparisonBarcodeFilters: filterToTreeItem(
        filters.value.competitorComparisonBarcodeFilters,
        'competitorComparisonBarcode'
      ),
      competitorComparisonItemFilters: filterToTreeItem(
        filters.value.competitorComparisonItemFilters,
        'competitorComparisonItem'
      ),
      competitorBrandsFilters: filterToTreeItem(filters.value.competitorBrandsFilters, 'competitorBrand'),
      consostyleMicroFilters: filterToTreeItem(filters.value.consostyleMicroFilters, 'consostyleMicro'),
      lifestageMicroFilters: filterToTreeItem(filters.value.lifestageMicroFilters, 'lifestageMicro'),
      businessBrandFilters: filterToTreeItem(filters.value.businessBrandFilters, 'businessBrand'),
      businessBrandReducedFilters: filterToTreeItem(filters.value.businessBrandReducedFilters, 'businessBrandReduced')
    };
  });
};
